<template>
  <div class="page container p-4 py-4">
    <div v-if="error" class="alert alert-danger text-center my-4">
      {{ error }}
      <div class="mt-3">
        <button class="btn btn-outline-danger btn-sm" @click="fetchFinanceData">
          Tentar novamente
        </button>
      </div>
    </div>

    <div v-else>
      <!-- Tour help button -->
      <div class="d-flex justify-content-end mb-2">
        <button class="fin-tour-btn" @click="startTour" title="Abrir guia da tela">
          <i class="fas fa-circle-question me-1"></i>Ver guia
        </button>
      </div>

      <!-- Compact balance header -->
      <div class="fin-balance-header mb-4" data-tour="balance-header">
        <div class="fin-balance-header__grid">
          <div class="fin-balance-header__stat">
            <span class="fin-balance-header__label">Previsto a receber</span>
            <span class="fin-balance-header__value">{{ formatCurrency(totalDisponivelReal) }}</span>
          </div>
          <div class="fin-balance-header__stat">
            <span class="fin-balance-header__label" title="Valores que serão liberados automaticamente em breve.">Aguardando liberação</span>
            <span class="fin-balance-header__value fin-balance-header__value--muted">{{ formatCurrency(totalBloqueado) }}</span>
          </div>
          <div v-if="totalEmTransferencia > 0" class="fin-balance-header__stat">
            <span class="fin-balance-header__label" title="Valores já solicitados e em envio para sua conta.">Em transferência</span>
            <span class="fin-balance-header__value fin-balance-header__value--transfer">{{ formatCurrency(totalEmTransferencia) }}</span>
          </div>
          <div v-if="totalFalhaNaTransferencia > 0" class="fin-balance-header__stat">
            <span class="fin-balance-header__label">Falha na transferência</span>
            <span class="fin-balance-header__value fin-balance-header__value--error">{{ formatCurrency(totalFalhaNaTransferencia) }}</span>
          </div>
          <div v-if="totalRecebido > 0" class="fin-balance-header__stat">
            <span class="fin-balance-header__label">Transferido</span>
            <span class="fin-balance-header__value fin-balance-header__value--transferred">{{ formatCurrency(totalRecebido) }}</span>
          </div>
        </div>
        <p class="fin-balance-header__collected">Total arrecadado: {{ formatCurrency(totalArrecadado) }}</p>
        <p v-if="receivableHeaderHint" class="fin-balance-header__hint">{{ receivableHeaderHint }}</p>
        <p v-if="showReceivableBreakdown" class="fin-balance-header__breakdown">
          Pix disponível: {{ formatCurrency(totalPixReceivable) }} · Cartão previsto: {{ formatCurrency(totalCardReceivable) }}
        </p>
      </div>

      <!-- Event Filter -->
      <div v-if="eventOptions.length > 1" class="event-filter mb-3" data-tour="event-filter">
        <p class="event-filter__label">FILTRAR POR EVENTO</p>
        <div class="event-filter__pills">
          <button
            class="event-pill"
            :class="{ 'event-pill--active': selectedEventId === 'all' }"
            @click="selectedEventId = 'all'"
          >
            <i class="fas fa-layer-group me-1"></i> Todos
          </button>
          <button
            v-for="evt in eventOptions"
            :key="evt.id"
            class="event-pill"
            :class="{ 'event-pill--active': selectedEventId === evt.id }"
            @click="selectedEventId = evt.id"
          >
            {{ evt.name }}
          </button>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="tab-nav mb-4">
        <button class="tab-btn" data-tour="tab-sacar" :class="{ active: activeFinTab === 'sacar' }" @click="activeFinTab = 'sacar'">Saldos</button>
        <button class="tab-btn" data-tour="tab-extrato" :class="{ active: activeFinTab === 'extrato' }" @click="activeFinTab = 'extrato'">Extrato</button>
        <button class="tab-btn" data-tour="tab-saques" :class="{ active: activeFinTab === 'saques' }" @click="onSaquesTabClick">Saques</button>
        <button class="tab-btn tab-btn--alerts" data-tour="tab-avisos" :class="{ active: activeFinTab === 'avisos' }" @click="onAvisosTabClick">
          Avisos
          <span v-if="filteredAlerts.length > 0" class="alerts-badge">{{ filteredAlerts.length }}</span>
        </button>
      </div>

      <!-- Tab: Saldos -->
      <SacarTab
        v-if="activeFinTab === 'sacar'"
        :event-balance-summary="filteredEventBalance"
        :requesting-withdrawal-for-event="requestingWithdrawalForEvent"
        :requesting-early-pix-for-event="requestingEarlyPixForEvent"
        :fixed-fee="fees?.transfer_fee_fixed ?? 5"
        :loading="loading"
        :account="account"
        :pix-account="pixAccount"
        :total-em-transferencia="totalEmTransferencia"
        :total-bloqueado="totalBloqueado"
        @request-withdrawal="handleRequestWithdrawal"
        @early-pix-withdrawal="handleEarlyPixWithdrawal"
        @retry="fetchFinanceData"
      />

      <!-- Tab: Extrato -->
      <ExtratoTab
        v-else-if="activeFinTab === 'extrato'"
        :entries="filteredLedgerEntries"
        :loading="loading"
        :initial-filter="ledgerFilter"
        @update:filter="ledgerFilter = $event"
      />

      <!-- Tab: Saques -->
      <SaquesTab
        v-else-if="activeFinTab === 'saques'"
        :withdrawals="filteredWithdrawals"
        :loading="loadingWithdrawals"
        :error="withdrawalsError"
        :initial-filter="withdrawalFilter"
        @update:filter="withdrawalFilter = $event"
        @retry-fetch="fetchWithdrawals"
      />

      <!-- Tab: Avisos -->
      <AvisosTab
        v-else-if="activeFinTab === 'avisos'"
        :alerts="filteredAlerts"
        :loading="loadingAlerts"
        :error="alertsError"
        @retry-fetch="fetchAlerts"
      />
    </div>
  </div>

  <TransferResultBottomModal
    :status="transferModalStatus"
    :netAmount="transferNetAmount"
    :errorMessage="transferErrorMsg"
    @close="closeTransferResultModal"
  />

  <BottomModal
    :visible="showRequestWithdrawalConfirmModal"
    @close="showRequestWithdrawalConfirmModal = false"
    :fullHeight="false"
    :persistent="isSubmittingWithdrawalRequest"
  >
    <div class="p-3">
      <h5 class="fw-bold mb-1 text-center">Confirmar solicitação de saque</h5>
      <p class="text-muted text-center mb-3" style="font-size: 0.85rem;">Verifique seus dados antes de continuar</p>

      <!-- Card TED / dados bancários -->
      <div class="withdrawal-data-card mb-2">
        <div class="withdrawal-data-card__header">
          <span class="withdrawal-data-card__label">Dados bancários (TED)</span>
          <button
            class="withdrawal-data-card__edit"
            @click="$router.push('/my-account/bank?tab=ted'); showRequestWithdrawalConfirmModal = false"
          >Editar</button>
        </div>
        <div v-if="account" class="withdrawal-data-card__body">
          <p class="withdrawal-data-card__name">{{ account.holder || '—' }}</p>
          <p class="withdrawal-data-card__detail">{{ account.bank || '—' }} · Ag {{ account.agency || '—' }} · Conta {{ account.account || '—' }}</p>
        </div>
        <div v-else class="withdrawal-data-card__empty">
          Dados bancários não cadastrados.
          <a href="#" class="withdrawal-data-card__empty-link"
            @click.prevent="$router.push('/my-account/bank?tab=ted'); showRequestWithdrawalConfirmModal = false">
            Cadastrar agora
          </a>
        </div>
      </div>

      <!-- Card Pix -->
      <div class="withdrawal-data-card mb-3">
        <div class="withdrawal-data-card__header">
          <span class="withdrawal-data-card__label">Chave Pix</span>
          <button
            class="withdrawal-data-card__edit"
            @click="$router.push('/my-account/bank?tab=pix'); showRequestWithdrawalConfirmModal = false"
          >{{ pixAccount ? 'Editar' : 'Cadastrar' }}</button>
        </div>
        <div v-if="pixAccount" class="withdrawal-data-card__body">
          <p class="withdrawal-data-card__name">{{ pixAccount.holder || '—' }}</p>
          <p class="withdrawal-data-card__detail">{{ pixKeyTypeLabel(pixAccount.agency) }}: {{ maskedPixAccountKey }}</p>
        </div>
        <div v-else class="withdrawal-data-card__empty">
          Nenhuma chave Pix cadastrada.
          <a href="#" class="withdrawal-data-card__empty-link"
            @click.prevent="$router.push('/my-account/bank?tab=pix'); showRequestWithdrawalConfirmModal = false">
            Cadastrar agora
          </a>
        </div>
      </div>

      <div class="d-flex gap-2 mt-3">
        <button
          class="btn btn-outline-secondary w-100"
          :disabled="isSubmittingWithdrawalRequest"
          @click="showRequestWithdrawalConfirmModal = false"
        >
          Solicitar saque depois
        </button>
        <button
          class="btn btn-color-site w-100"
          :disabled="isSubmittingWithdrawalRequest || !pixAccount"
          @click="confirmPendingWithdrawal"
        >
          <span v-if="isSubmittingWithdrawalRequest" class="spinner-border spinner-border-sm me-2" />
          {{ isSubmittingWithdrawalRequest ? 'Enviando...' : 'Confirmar saque' }}
        </button>
      </div>
    </div>
  </BottomModal>

  <BottomModal
    :visible="showPixRequiredModal"
    @close="showPixRequiredModal = false"
    :fullHeight="false"
    :showCloseButton="true"
  >
    <div class="p-3 text-center">
      <div class="mb-3">
        <i class="fas fa-piggy-bank text-warning" style="font-size: 2.5rem;"></i>
      </div>
      <h5 class="fw-bold mb-2">Cadastre seus dados bancários</h5>
      <p class="text-muted mb-4" style="font-size: 0.9rem;">
        Para realizar a transferência, você precisa cadastrar seus dados bancários antes.
      </p>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary w-100" @click="showPixRequiredModal = false">
          Solicitar saque depois
        </button>
        <button
          class="btn btn-color-site w-100"
          @click="$router.push('/my-account/bank'); showPixRequiredModal = false"
        >
          Cadastrar dados bancários
        </button>
      </div>
    </div>
  </BottomModal>

  <FullScreenLoader v-if="loading" />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { maskPixKey } from '@/utils/pixHelpers';
import BottomModal from '@/components/BottomModal.vue';
import TransferResultBottomModal from '@/components/TransferResultBottomModal/TransferResultBottomModal.vue';
import FullScreenLoader from '@/components/FullScreenLoader.vue';
import SacarTab from '@/components/finance/SacarTab.vue';
import ExtratoTab from '@/components/finance/ExtratoTab.vue';
import SaquesTab from '@/components/finance/SaquesTab.vue';
import AvisosTab from '@/components/finance/AvisosTab.vue';
import { useWithdrawalStore } from '@/stores/withdrawalStore';
import { useAuthStore } from '@/stores/authStore';
import { getFees } from '@/services/feesService';
import {
  getRetryStatus, getEventBalanceSummary, createWithdrawalRequest, earlyPixWithdrawal,
  getFinanceAlerts, reportWithdrawalToSupport,
  getFinanceSummary, getLedgerEntries, getWithdrawals,
  getActiveBankDetails, getFinancePayments, getAccountReceiver,
} from '@/services/paymentService';
import { FINANCE_POLL_INTERVAL_MS } from '@/config/financeConfig';
import { useFinanceTour } from '@/composables/useFinanceTour';
import { calculateFinanceHeaderBuckets, toFiniteNumber } from '@/utils/financeBuckets';

const withdrawalStore = useWithdrawalStore();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

// ── State ──
const account = ref(null);
const payments = ref([]);
const ledgerEntries = ref([]);
const eventBalanceSummary = ref([]);
const alerts = ref([]);
const loadingAlerts = ref(false);
const requestingWithdrawalForEvent = ref(null);
const reportingToSupportFor = ref(null);
const requestingEarlyPixForEvent = ref(null);
const summaryData = ref(null);
const loading = ref(true);
const activeFinTab = ref(route.query.tab || 'sacar');
const withdrawals = ref([]);
const loadingWithdrawals = ref(false);
const error = ref('');
const withdrawalsError = ref('');
const alertsError = ref('');
const fees = ref(null);
const selectedEventId = ref(route.query.event || 'all');
const retryStatus = ref({
  has_pending: false,
  has_permanently_failed: false,
  has_pending_transfer: false,
  total_pending_amount: 0,
  transfer_fee: 0,
  net_amount: 0,
  transfer_blocked_until: null,
});
const transferModalStatus = ref(null);
const transferNetAmount = ref(undefined);
const transferErrorMsg = ref('');
const pixAccount = ref(null);
const showPixRequiredModal = ref(false);
const showRequestWithdrawalConfirmModal = ref(false);

const pendingWithdrawalEventId = ref(null);
const pendingWithdrawalRequestType = ref(null);
const isSubmittingWithdrawalRequest = ref(false);
let inFlightPollTimer = null;
let isPollingInProgress = false;

// Filters (kept for URL sync — children emit changes back)
const ledgerFilter = ref(route.query.ledger || 'all');
const withdrawalFilter = ref(route.query.wstatus || 'all');
const ACTIVE_RECEIVABLE_TRANSFER_STATUSES = ['pending', 'approved', 'processing'];

// ── Polling ──
function startInFlightPolling() {
  stopInFlightPolling();
  inFlightPollTimer = setInterval(async () => {
    const hasInFlight = eventBalanceSummary.value.some(
      (item) => item.withdrawal_request_status && ['pending', 'approved', 'processing'].includes(item.withdrawal_request_status)
    );
    if (!hasInFlight) {
      stopInFlightPolling();
      return;
    }
    await refreshWithdrawalData();
  }, FINANCE_POLL_INTERVAL_MS);
}

function stopInFlightPolling() {
  if (inFlightPollTimer) {
    clearInterval(inFlightPollTimer);
    inFlightPollTimer = null;
  }
}

// ── Computed ──
const transferFeeValue = computed(() => {
  const value = Number(fees.value?.transfer_fee_fixed);
  return Number.isFinite(value) ? value : null;
});

const transferFeeForBanner = computed(() => {
  const value = Number(fees.value?.transfer_fee_fixed);
  return Number.isFinite(value) ? value : 5;
});

const isTransferInProgress = computed(() =>
  retryStatus.value.has_pending_transfer || withdrawalStore.isTransferBlocked
);

// ── Event filter ──
const eventOptions = computed(() => {
  const seen = new Set();
  return eventBalanceSummary.value
    .filter(e => {
      if (seen.has(e.event_id)) return false;
      seen.add(e.event_id);
      return true;
    })
    .map(e => ({ id: e.event_id, name: e.event_name }));
});

const filteredEventBalance = computed(() =>
  selectedEventId.value === 'all'
    ? eventBalanceSummary.value
    : eventBalanceSummary.value.filter(e => e.event_id === selectedEventId.value)
);

const filteredLedgerEntries = computed(() =>
  selectedEventId.value === 'all'
    ? ledgerEntries.value
    : ledgerEntries.value.filter(e => e.event_id === selectedEventId.value)
);

const filteredAlerts = computed(() =>
  selectedEventId.value === 'all'
    ? alerts.value
    : alerts.value.filter(a => a.event_id === selectedEventId.value)
);

const filteredWithdrawals = computed(() =>
  selectedEventId.value === 'all'
    ? withdrawals.value
    : withdrawals.value.filter(w => w.event_id === selectedEventId.value)
);

const financeHeaderBuckets = computed(() => calculateFinanceHeaderBuckets({
  eventBalanceSummary: filteredEventBalance.value,
  ledgerEntries: filteredLedgerEntries.value,
  summaryData: summaryData.value,
  preferSummaryTotals: selectedEventId.value === 'all',
}));

const totalArrecadado = computed(() => financeHeaderBuckets.value.grossCollected);
const totalBloqueado = computed(() => financeHeaderBuckets.value.waitingRelease);
const totalEmTransferencia = computed(() => financeHeaderBuckets.value.inTransfer);
const totalFalhaNaTransferencia = computed(() => financeHeaderBuckets.value.failedTransfer);
const totalRecebido = computed(() => financeHeaderBuckets.value.received);
const totalDisponivelReal = computed(() => financeHeaderBuckets.value.receivable);

function pixReceivableAmountForItem(item) {
  if (item.pix) return toFiniteNumber(item.pix.available_to_withdraw);
  if (item.totals) return 0;

  const activeTransferAmount = ACTIVE_RECEIVABLE_TRANSFER_STATUSES.includes(item.withdrawal_request_status)
    ? toFiniteNumber(item.withdrawal_request_amount)
    : 0;
  return Math.max(toFiniteNumber(item.available_amount_pix) - activeTransferAmount, 0);
}

function cardReceivableAmountForItem(item) {
  if (item.card) return toFiniteNumber(item.card.pending_payout);
  if (item.totals) return 0;
  return toFiniteNumber(item.available_amount_card);
}

const totalPixReceivable = computed(() => filteredEventBalance.value.reduce(
  (sum, item) => sum + pixReceivableAmountForItem(item),
  0,
));

const totalCardReceivable = computed(() => filteredEventBalance.value.reduce(
  (sum, item) => sum + cardReceivableAmountForItem(item),
  0,
));

const hasPixReceivable = computed(() => totalPixReceivable.value > 0);
const hasCardReceivable = computed(() => totalCardReceivable.value > 0);

const showReceivableBreakdown = computed(() => {
  if (!hasPixReceivable.value || !hasCardReceivable.value) return false;
  const splitTotal = totalPixReceivable.value + totalCardReceivable.value;
  return Math.abs(splitTotal - totalDisponivelReal.value) < 0.01;
});

const receivableHeaderHint = computed(() => {
  if (totalDisponivelReal.value <= 0) return '';
  if (hasPixReceivable.value && hasCardReceivable.value) {
    return 'Inclui Pix disponível para resgate e repasses de cartão previstos.';
  }
  if (hasPixReceivable.value) return 'Inclui Pix disponível para resgate.';
  if (hasCardReceivable.value) return 'Inclui repasses de cartão previstos.';
  return 'Valores previstos para recebimento.';
});

const maskedPixAccountKey = computed(() => {
  const keyType = pixAccount.value?.agency || pixAccount.value?.key_type;
  const masked = maskPixKey(pixAccount.value?.account || pixAccount.value?.pix_key, keyType);
  return masked || '—';
});

// ── Helpers ──
function pixKeyTypeLabel(keyType) {
  const labels = {
    CPF: 'CPF',
    CNPJ: 'CNPJ',
    EMAIL: 'E-mail',
    PHONE: 'Celular',
    EVP: 'Chave aleatória',
  };
  return labels[String(keyType || '').toUpperCase()] || keyType || 'Chave Pix';
}

// ── Data fetching ──
async function fetchRetryStatus() {
  try {
    const status = await getRetryStatus();
    retryStatus.value = status;
    withdrawalStore.setFromRetryStatus(status);
  } catch {
    retryStatus.value = {
      has_pending: false,
      has_permanently_failed: false,
      has_pending_transfer: false,
      total_pending_amount: 0,
      transfer_fee: 0,
      net_amount: 0,
      transfer_blocked_until: null,
    };
  }
}

async function refreshWithdrawalData() {
  if (isPollingInProgress || requestingWithdrawalForEvent.value || requestingEarlyPixForEvent.value) return;
  isPollingInProgress = true;
  try {
    const [summaryRes, ledgerRes, eventBalanceRes] = await Promise.allSettled([
      getFinanceSummary(),
      getLedgerEntries(),
      getEventBalanceSummary(),
    ]);
    if (summaryRes.status === 'fulfilled') summaryData.value = summaryRes.value;
    if (ledgerRes.status === 'fulfilled' && Array.isArray(ledgerRes.value)) {
      ledgerEntries.value = ledgerRes.value;
    }
    if (eventBalanceRes.status === 'fulfilled' && Array.isArray(eventBalanceRes.value)) {
      eventBalanceSummary.value = eventBalanceRes.value;
    }
    const allFailed = [summaryRes, ledgerRes, eventBalanceRes].every(r => r.status === 'rejected');
    if (allFailed) {
      toast.warning('Não foi possível atualizar os dados. Tentaremos novamente em breve.');
    }
  } catch {
    toast.warning('Erro ao atualizar dados financeiros.');
  } finally {
    isPollingInProgress = false;
  }
}

async function fetchWithdrawals() {
  loadingWithdrawals.value = true;
  withdrawalsError.value = '';
  try {
    const res = await getWithdrawals();
    withdrawals.value = res;
  } catch (err) {
    withdrawals.value = [];
    // NOT_FOUND significa que nenhum saque foi feito ainda — exibe estado vazio, não erro
    const code = err?.response?.data?.code;
    if (code !== 'NOT_FOUND') {
      withdrawalsError.value = 'Não foi possível carregar os saques. Tente novamente.';
    }
  } finally {
    loadingWithdrawals.value = false;
  }
}

async function fetchAlerts() {
  loadingAlerts.value = true;
  alertsError.value = '';
  try {
    alerts.value = await getFinanceAlerts();
  } catch {
    alerts.value = [];
    alertsError.value = 'Não foi possível carregar os avisos. Tente novamente.';
  } finally {
    loadingAlerts.value = false;
  }
}

async function fetchFinanceData() {
  loading.value = true;
  error.value = '';
  try {
    const [accountRes, paymentsRes, feesRes, summaryRes, ledgerRes, eventBalanceRes, pixAccountRes, alertsRes] = await Promise.allSettled([
      getActiveBankDetails(),
      getFinancePayments(),
      getFees(),
      getFinanceSummary(),
      getLedgerEntries(),
      getEventBalanceSummary(),
      getAccountReceiver(),
      getFinanceAlerts(),
    ]);

    if (accountRes.status === 'fulfilled') {
      const details = accountRes.value || {};
      const hasBank = Boolean(details?.has_bank ?? details?.hasBank);
      const normalizedStatus = String(details?.status || '').trim().toLowerCase();

      let mappedStatus = hasBank ? 'verified' : 'pending';
      if (normalizedStatus === 'errored' || normalizedStatus === 'invalid') {
        mappedStatus = 'invalid';
      } else if (hasBank || normalizedStatus === 'validated' || normalizedStatus === 'verified') {
        mappedStatus = 'verified';
      }

      account.value = hasBank
        ? {
            bank: String(details?.bank_name ?? details?.bankName ?? 'Banco cadastrado'),
            agency: String(details?.agency || '---'),
            account: String(details?.account_masked ?? details?.accountMasked ?? '******'),
            holder: String(details?.account_holder ?? details?.accountHolder ?? 'Nao informado'),
            status: mappedStatus,
          }
        : null;
    } else {
      account.value = null;
    }

    payments.value = paymentsRes.status === 'fulfilled'
      && Array.isArray(paymentsRes.value)
      ? paymentsRes.value
      : [];

    fees.value = feesRes.status === 'fulfilled'
      ? feesRes.value
      : null;

    summaryData.value = summaryRes.status === 'fulfilled'
      ? summaryRes.value
      : null;

    ledgerEntries.value = ledgerRes.status === 'fulfilled'
      && Array.isArray(ledgerRes.value)
      ? ledgerRes.value
      : [];

    eventBalanceSummary.value = eventBalanceRes.status === 'fulfilled'
      && Array.isArray(eventBalanceRes.value)
      ? eventBalanceRes.value
      : [];

    if (pixAccountRes.status === 'fulfilled') {
      const pix = pixAccountRes.value;
      pixAccount.value = pix?.account ? pix : null;
    } else {
      pixAccount.value = null;
    }

    alerts.value = alertsRes.status === 'fulfilled'
      && Array.isArray(alertsRes.value)
      ? alertsRes.value
      : [];

    const authError = [accountRes, paymentsRes].find(
      r => r.status === 'rejected' && r.reason?.response?.status === 401
    );
    if (authError) {
      error.value = 'Sua sessão expirou. Faça login novamente.';
      return;
    }

    const criticalFinanceUnavailable = [summaryRes, ledgerRes, eventBalanceRes].every(
      (result) => result.status === 'rejected'
    );
    if (criticalFinanceUnavailable) {
      error.value = 'Não foi possível carregar os dados financeiros. Tente novamente.';
    }
  } catch (err) {
    error.value = err.response?.status === 401
      ? 'Sua sessão expirou. Faça login novamente.'
      : 'Não foi possível carregar os dados financeiros. Tente novamente.';
  } finally {
    loading.value = false;
  }
}

// ── Withdrawal actions ──
async function handleRequestWithdrawal(eventId, requestType) {
  if (requestingWithdrawalForEvent.value) return;

  pendingWithdrawalEventId.value = eventId;
  pendingWithdrawalRequestType.value = requestType;
  showRequestWithdrawalConfirmModal.value = true;
}

async function handleEarlyPixWithdrawal(eventId) {
  if (requestingEarlyPixForEvent.value) return;
  requestingEarlyPixForEvent.value = eventId;
  try {
    await earlyPixWithdrawal(eventId);
    toast.success('Saque antecipado solicitado. A transferência será processada em breve.');
    // Atualização otimista: marca o evento como "pending" imediatamente,
    // impedindo o botão de reaparecer antes do refresh completar.
    const idx = eventBalanceSummary.value.findIndex(s => s.event_id === eventId);
    if (idx !== -1) {
      eventBalanceSummary.value = eventBalanceSummary.value.map((s, i) =>
        i === idx ? { ...s, withdrawal_request_status: 'pending' } : s
      );
    }
    // Libera o guard ANTES do refresh — refreshWithdrawalData bloqueia quando a ref está setada.
    requestingEarlyPixForEvent.value = null;
    await refreshWithdrawalData();
  } catch (err) {
    const msg = err?.response?.data?.error || 'Erro ao solicitar saque antecipado. Tente novamente.';
    toast.error(msg);
    requestingEarlyPixForEvent.value = null;
    await Promise.allSettled([fetchAlerts(), refreshWithdrawalData()]);
  } finally {
    requestingEarlyPixForEvent.value = null;
  }
}

async function confirmPendingWithdrawal() {
  if (isSubmittingWithdrawalRequest.value) return;
  isSubmittingWithdrawalRequest.value = true;
  requestingWithdrawalForEvent.value = pendingWithdrawalEventId.value;
  showRequestWithdrawalConfirmModal.value = false;

  const eventId = pendingWithdrawalEventId.value;
  const reqType = pendingWithdrawalRequestType.value;

  try {
    await createWithdrawalRequest(eventId, reqType);
    toast.success('Estamos processando sua transferência. Em breve o valor estará em sua conta.');
    const idx = eventBalanceSummary.value.findIndex(s => s.event_id === eventId);
    if (idx !== -1) {
      eventBalanceSummary.value = eventBalanceSummary.value.map((s, i) =>
        i === idx ? { ...s, withdrawal_request_status: 'pending' } : s
      );
    }
  } catch (err) {
    toast.error('Não foi possível processar a transferência. Tente novamente em instantes.');
    await fetchAlerts();
  } finally {
    requestingWithdrawalForEvent.value = null;
    pendingWithdrawalEventId.value = null;
    pendingWithdrawalRequestType.value = null;
    isSubmittingWithdrawalRequest.value = false;
  }
  // Atualiza dados financeiros em segundo plano
  await refreshWithdrawalData();
}

async function handleReportToSupport(alert) {
  reportingToSupportFor.value = alert.withdrawal_request_id;
  try {
    await reportWithdrawalToSupport(alert.withdrawal_request_id);
    // Atualiza o alert localmente para mostrar o estado "notificado" imediatamente
    const idx = alerts.value.findIndex(a => a.withdrawal_request_id === alert.withdrawal_request_id);
    if (idx !== -1) {
      alerts.value[idx] = { ...alerts.value[idx], support_notified_at: new Date().toISOString() };
    }
  } catch (err) {
    const msg = err?.response?.data?.error || err?.message || 'Erro ao notificar suporte.';
    transferModalStatus.value = 'error';
    transferErrorMsg.value = msg;
  } finally {
    reportingToSupportFor.value = null;
  }
}

function closeTransferResultModal() {
  transferModalStatus.value = null;
  transferNetAmount.value = undefined;
  transferErrorMsg.value = '';
}

// ── Tab navigation ──
function onSaquesTabClick() {
  activeFinTab.value = 'saques';
  if (!withdrawals.value.length && !loadingWithdrawals.value) fetchWithdrawals();
}

function onAvisosTabClick() {
  activeFinTab.value = 'avisos';
  fetchAlerts();
}

// ── Watchers ──
watch(
  () => eventBalanceSummary.value.some(
    (item) => item.withdrawal_request_status && ['pending', 'approved', 'processing'].includes(item.withdrawal_request_status)
  ),
  (hasInFlight) => {
    if (hasInFlight) startInFlightPolling();
    else stopInFlightPolling();
  },
  { immediate: false }
);

// Sincroniza aba e filtros com a URL (replace para não poluir o histórico)
watch([activeFinTab, ledgerFilter, withdrawalFilter, selectedEventId], ([tab, ledger, wstatus, event]) => {
  const query = { tab };
  if (ledger !== 'all') query.ledger = ledger;
  if (wstatus !== 'all') query.wstatus = wstatus;
  if (event !== 'all') query.event = event;
  router.replace({ query });
}, { flush: 'post' });

// Ao trocar de aba, reseta o filtro da aba anterior para evitar estado orfao
watch(activeFinTab, (newTab, oldTab) => {
  if (oldTab === 'extrato') ledgerFilter.value = 'all';
  if (oldTab === 'saques') withdrawalFilter.value = 'all';
});

const { startTour, startTourIfFirstVisit } = useFinanceTour();

// ── Lifecycle ──
onMounted(async () => {
  await Promise.allSettled([
    fetchFinanceData(),
    fetchRetryStatus(),
  ]);

  startTourIfFirstVisit();

  // Se a URL já tem tab=saques ou tab=avisos, dispara o fetch correspondente
  const initialTab = route.query.tab;
  if (initialTab === 'saques') onSaquesTabClick();
  if (initialTab === 'avisos') fetchAlerts();
});

onBeforeUnmount(() => {
  stopInFlightPolling();
});
</script>

<style scoped>
.description-text { font-size: 0.95rem; line-height: 1.5; color: #6c757d; }

/* Tour help button */
.fin-tour-btn {
  background: none;
  border: none;
  color: #ccc;
  font-size: 0.78rem;
  cursor: pointer;
  padding: 2px 4px;
  transition: color 0.15s;
  letter-spacing: 0.02em;
}
.fin-tour-btn:hover {
  color: #e91e8c;
}

/* --- Withdrawal data cards (modal de confirmação de saque) --- */
.withdrawal-data-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fff;
}
.withdrawal-data-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.withdrawal-data-card__label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}
.withdrawal-data-card__edit {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.82rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.withdrawal-data-card__edit:hover { color: #111827; }
.withdrawal-data-card__name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
}
.withdrawal-data-card__detail {
  font-size: 0.83rem;
  color: #6b7280;
  margin-bottom: 0;
}
.withdrawal-data-card__empty {
  font-size: 0.83rem;
  color: #9ca3af;
}
.withdrawal-data-card__empty-link {
  color: #111827;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* ── Tab Navigation ── */
.tab-nav {
  display: flex;
  border-bottom: 2px solid #e9ecef;
  gap: 0;
  margin-bottom: 0;
}
.tab-btn {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0.55rem 1.1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.tab-btn:hover { color: #374151; }
.tab-btn.active {
  color: #ff50a3;
  border-bottom-color: #ff50a3;
  font-weight: 600;
}
.tab-btn--alerts { position: relative; }
.alerts-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #dc3545;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  margin-left: 5px;
  vertical-align: middle;
}

/* ── Compact balance header ── */
.fin-balance-header {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px 20px 12px;
}
.fin-balance-header__grid {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.fin-balance-header__stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fin-balance-header__label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
}
.fin-balance-header__value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}
.fin-balance-header__value--muted { color: #9ca3af; }
.fin-balance-header__value--transfer { color: #2563eb; }
.fin-balance-header__value--transferred { color: #16a34a; }
.fin-balance-header__value--error { color: #dc2626; }
.fin-balance-header__collected {
  font-size: 0.78rem;
  color: #9ca3af;
  margin: 0;
}
.fin-balance-header__hint {
  font-size: 0.74rem;
  color: #6b7280;
  margin: 4px 0 0;
}
.fin-balance-header__breakdown {
  font-size: 0.74rem;
  color: #4b5563;
  margin: 2px 0 0;
}

/* ── Page layout ── */
.pb-6 { padding-bottom: 3rem; }

.page {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  width: 100%;
  max-width: 750px !important;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .page { width: 100%; }
}

@media (max-width: 767.98px) {
  .page { margin-top: 0rem !important; min-width: inherit; }
}

.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
}

.btn-color-site:hover,
.btn-color-site:focus,
.btn-color-site:active,
.btn-color-site:focus-visible {
  background-color: #e8417f;
  color: #fff;
  border-color: #e8417f;
}

/* ── Event Filter Pills ── */
.event-filter {
  width: 100%;
}
.event-filter__label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #9e9e9e;
  margin-bottom: 10px;
}
.event-filter__pills {
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
}
/* Mobile: largura absoluta em vw impede o flex container de expandir o layout.
   overflow-x:auto só funciona com largura definida, não percentual. */
@media (max-width: 767.98px) {
  .event-filter__pills {
    width: calc(100vw - 3rem);
    max-width: calc(100vw - 3rem);
  }
}
.event-filter__pills::-webkit-scrollbar {
  display: none;
}
.event-pill {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 6px 16px;
  border-radius: 99px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
}
.event-pill:hover {
  background: #ff50a3;
  border-color: #ff50a3;
  color: #fff;
}
.event-pill--active {
  background: #ff50a3;
  border-color: #ff50a3;
  color: #fff;
}
</style>
